import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useSchool } from '../context/SchoolContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Save, Calendar, X, Plus, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE } from '../apiConfig';

const API = API_BASE;

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
const DAY_COLORS = [
  'bg-blue-50 border-blue-200 text-blue-700',
  'bg-green-50 border-green-200 text-green-700',
  'bg-yellow-50 border-yellow-200 text-yellow-700',
  'bg-purple-50 border-purple-200 text-purple-700',
  'bg-cyan-50 border-cyan-200 text-cyan-700',
];

export const TimetablePage = () => {
  const { selectedSchool } = useSchool();
  const [classes, setClasses] = useState([]);
  const [timetable, setTimetable] = useState({}); // { "day_period": classId }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addClassOpen, setAddClassOpen] = useState(false);
  const [newClassName, setNewClassName] = useState('');
  const [newClassCode, setNewClassCode] = useState('');

  const periodsPerDay = selectedSchool?.periods_per_day || 6;

  const fetchData = async () => {
    if (!selectedSchool) return;
    setLoading(true);
    try {
      const [classesRes, timetableRes] = await Promise.all([
        fetch(`${API}/classes?school_id=${selectedSchool.school_id}`, { credentials: 'include' }),
        fetch(`${API}/timetable/${selectedSchool.school_id}`, { credentials: 'include' })
      ]);
      if (classesRes.ok) {
        const allClasses = await classesRes.json();
        setClasses(allClasses.filter(c => !c.archived));
      }
      if (timetableRes.ok) {
        const entries = await timetableRes.json();
        const map = {};
        entries.forEach(e => { map[`${e.day}_${e.period}`] = e.class_id; });
        setTimetable(map);
      }
    } catch (error) { console.error('Failed to load timetable:', error); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [selectedSchool]);

  const handleCellChange = (day, period, classId) => {
    const key = `${day}_${period}`;
    setTimetable(prev => {
      const copy = { ...prev };
      if (!classId || classId === 'clear') delete copy[key];
      else copy[key] = classId;
      return copy;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const entries = Object.entries(timetable).map(([key, classId]) => {
        const [day, period] = key.split('_').map(Number);
        return { day, period, class_id: classId };
      });
      const r = await fetch(`${API}/timetable/${selectedSchool.school_id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ entries })
      });
      if (r.ok) toast.success('Timetable saved');
      else toast.error('Failed to save timetable');
    } catch (error) { toast.error('Failed to save timetable'); }
    finally { setSaving(false); }
  };

  const getClassName = (classId) => {
    const cls = classes.find(c => c.class_id === classId);
    return cls ? cls.name : '';
  };

  const handleAddClass = async () => {
    if (!newClassName.trim()) { toast.error('Class name is required'); return; }
    try {
      const r = await fetch(`${API}/classes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ name: newClassName.trim(), class_code: newClassCode.trim(), school_id: selectedSchool.school_id })
      });
      if (r.ok) {
        toast.success('Class created');
        setAddClassOpen(false);
        setNewClassName('');
        setNewClassCode('');
        fetchData();
      } else { toast.error('Failed to create class'); }
    } catch (error) { toast.error('Failed to create class'); }
  };

  const handleReset = async () => {
    if (!window.confirm('Clear the entire timetable? This will remove all class assignments for every day.')) return;
    try {
      const emptyTimetable = {};
      DAYS.forEach(d => {
        emptyTimetable[d] = {};
        for (let p = 1; p <= periodsPerDay; p++) emptyTimetable[d][p] = '';
      });
      setTimetable(emptyTimetable);
      const entries = [];
      const r = await fetch(`${API}/timetable`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ school_id: selectedSchool.school_id, entries }),
      });
      if (r.ok) toast.success('Timetable cleared');
      else toast.error('Failed to clear timetable');
    } catch (error) { toast.error('Failed to clear timetable'); }
  };

  const todayIndex = new Date().getDay() - 1; // 0=Mon

  return (
    <Layout title="Timetable" breadcrumb={[{ label: 'Timetable' }]}>
      <div className="space-y-5" data-testid="timetable-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-medium text-google-grey-900">My Timetable</h1>
            <p className="text-google-grey-600 mt-1">{selectedSchool?.name} &middot; {periodsPerDay} periods per day</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleReset}
              className="border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600"
              data-testid="reset-timetable-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" />Reset
            </Button>
            <Button variant="outline" onClick={() => setAddClassOpen(true)}
              className="border-slate-200 text-slate-700 hover:bg-slate-50"
              data-testid="add-class-shortcut-btn"
            >
              <Plus className="w-4 h-4 mr-2" />Add Class
            </Button>
            <Button onClick={handleSave} disabled={saving}
              className="bg-google-blue hover:bg-blue-600 text-white shadow-google hover:shadow-google-hover transition-shadow"
              data-testid="save-timetable-btn"
            >
              <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-google-grey-500">Loading...</div>
        ) : (
          <Card className="bg-white border border-google-grey-200 overflow-hidden">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse min-w-[640px]">
                  <thead>
                    <tr>
                      <th className="w-20 p-3 text-left text-xs font-medium text-google-grey-500 uppercase tracking-wider border-b border-google-grey-200 bg-google-grey-50">
                        Period
                      </th>
                      {DAYS.map((day, i) => (
                        <th
                          key={day}
                          className={`p-3 text-center text-sm font-medium border-b border-google-grey-200 ${
                            i === todayIndex ? 'bg-blue-50 text-google-blue' : 'bg-google-grey-50 text-google-grey-700'
                          }`}
                        >
                          <span className="hidden sm:inline">{day}</span>
                          <span className="sm:hidden">{DAY_SHORT[i]}</span>
                          {i === todayIndex && <span className="ml-1.5 inline-block w-1.5 h-1.5 bg-google-blue rounded-full" />}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: periodsPerDay }, (_, periodIndex) => {
                      const period = periodIndex + 1;
                      return (
                        <tr key={period} className="border-b border-google-grey-100 last:border-b-0">
                          <td className="p-3 bg-google-grey-50">
                            <div className="flex items-center gap-2">
                              <span className="w-7 h-7 rounded-lg bg-google-grey-200 flex items-center justify-center text-sm font-bold text-google-grey-600">
                                {period}
                              </span>
                            </div>
                          </td>
                          {DAYS.map((day, dayIndex) => {
                            const key = `${dayIndex}_${period}`;
                            const assignedClassId = timetable[key];
                            const isToday = dayIndex === todayIndex;

                            return (
                              <td
                                key={key}
                                className={`p-2 ${isToday ? 'bg-blue-50/30' : ''}`}
                                data-testid={`cell-${dayIndex}-${period}`}
                              >
                                {assignedClassId ? (
                                  <div className={`rounded-lg border p-2.5 ${DAY_COLORS[dayIndex]} group relative`}>
                                    <p className="text-sm font-medium truncate pr-5">{getClassName(assignedClassId)}</p>
                                    {classes.find(c => c.class_id === assignedClassId)?.class_code && (
                                      <p className="text-xs opacity-70 truncate">
                                        {classes.find(c => c.class_id === assignedClassId)?.class_code}
                                      </p>
                                    )}
                                    <button
                                      onClick={() => handleCellChange(dayIndex, period, 'clear')}
                                      className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                                      data-testid={`clear-cell-${dayIndex}-${period}`}
                                    >
                                      <X className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                ) : (
                                  <Select onValueChange={(val) => handleCellChange(dayIndex, period, val)}>
                                    <SelectTrigger
                                      className="h-10 border-dashed border-google-grey-200 text-google-grey-400 text-sm bg-transparent hover:bg-google-grey-50"
                                      data-testid={`select-${dayIndex}-${period}`}
                                    >
                                      <SelectValue placeholder="—" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {classes.map(cls => (
                                        <SelectItem key={cls.class_id} value={cls.class_id}>
                                          <span className="font-medium">{cls.name}</span>
                                          {cls.class_code && <span className="text-google-grey-400 ml-1.5">({cls.class_code})</span>}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        <p className="text-sm text-google-grey-500 text-center">
          Select a class from the dropdown for each period. Leave blank if not teaching that period.
        </p>

        {/* Add Class Dialog */}
        <Dialog open={addClassOpen} onOpenChange={setAddClassOpen}>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-slate-900">Add New Class</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Class Name *</label>
                <Input placeholder="e.g., Year 7 English" value={newClassName} onChange={(e) => setNewClassName(e.target.value)} className="mt-1 border-slate-200" data-testid="new-class-name-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Class Code (optional)</label>
                <Input placeholder="e.g., 7ENG-A" value={newClassCode} onChange={(e) => setNewClassCode(e.target.value)} className="mt-1 border-slate-200" data-testid="new-class-code-input" />
              </div>
              <Button onClick={handleAddClass} className="w-full bg-teal-600 hover:bg-teal-700 text-white" data-testid="create-class-btn">Create Class</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
